import React from 'react';
import { connect } from 'react-redux';
import { Card, Tab, Tabs } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import {
  ccsdChangesBulkCheck,
  ccsdChangesCheck,
  ccsdChangesDelete,
  ccsdChangesListFetch,
  ccsdChangesListSetPage,
  ccsdChangesListUnload,
  ccsdChangesUncheck,
} from '../../lib/redux/actions/ccsdChanges';
import { isSeniorStaff } from '../../lib/helper/authorisation';
import DeleteCostChangeModal from '../../components/ccsdCodeCost/DeleteCostChangeModal';
import MASpinner from '../../components/common/MASpinner';
import Paginator from '../../components/common/Paginator';
import * as ROUTES from '../../lib/routing/frontend';
import CcsdChangesTable from './CcsdChangesTable';
import CcsdChangesFilterForm from '../../components/forms/CcsdCode/CcsdChangesFilterForm';
import CcsdDefaultChangesTab from './CcsdDefaultChangesTab';
import { getFiltersFromSession } from '../../lib/routing/filters';

const DEFAULT_FILTERS = {
  'exists[ccsdCodeCost]': true,
  'exists[checkedAt]': false,
  'order[createdAt]': 'asc',
};

const mapStateToProps = (state) => ({
  ...state.ccsdChanges,
});

const mapDispatchToProps = {
  ccsdChangesListFetch,
  ccsdChangesListSetPage,
  ccsdChangesListUnload,
  ccsdChangesCheck,
  ccsdChangesUncheck,
  ccsdChangesBulkCheck,
  ccsdChangesDelete,
};

class CcsdChangesContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { showFilters: false, activeTab: 'consultant', pendingDelete: null, isDeleting: false };
  }

  getCombinedFilters() {
    const { filters, match } = this.props;
    const filtersFromSession = getFiltersFromSession(match.path, 'json');
    const merged = { ...DEFAULT_FILTERS, ...filters, ...filtersFromSession };
    Object.keys(merged).forEach((key) => (merged[key] === null || key === 'showChecked') && delete merged[key]);
    return merged;
  }

  componentWillUnmount() {
    this.props.ccsdChangesListUnload();
  }

  componentDidMount() {
    this.props.ccsdChangesListFetch(this.getQueryParamPage(), this.getCombinedFilters());
  }

  componentDidUpdate(prevProps) {
    const { currentPage, ccsdChangesListFetch, ccsdChangesListSetPage, filters } = this.props;

    if (prevProps.match.params.page !== this.getQueryParamPage()) {
      ccsdChangesListSetPage(this.getQueryParamPage());
    }
    if (prevProps.currentPage !== currentPage || prevProps.filters !== filters) {
      ccsdChangesListFetch(currentPage, this.getCombinedFilters());
    }
  }

  getQueryParamPage() {
    return Number(this.props.match.params.page) || 1;
  }

  changePage(page) {
    const { history, ccsdChangesListSetPage } = this.props;
    ccsdChangesListSetPage(page);
    history.push(ROUTES.CCSD_CHANGES.ALL.replace(':page?', page));
  }

  nextPage() {
    const { currentPage, pageCount } = this.props;
    this.changePage(Math.min(currentPage + 1, pageCount));
  }

  prevPage() {
    const { currentPage } = this.props;
    this.changePage(Math.max(currentPage - 1, 1));
  }

  toggleFilters() {
    this.setState((prev) => ({ showFilters: !prev.showFilters }));
  }

  setActiveTab(tab) {
    this.setState({ activeTab: tab });
  }

  handleBulkCheck(ids) {
    const { ccsdChangesBulkCheck, currentPage } = this.props;
    ccsdChangesBulkCheck(ids, currentPage, this.getCombinedFilters());
  }

  confirmDelete() {
    const { ccsdChangesDelete, currentPage } = this.props;
    const { pendingDelete } = this.state;

    if (!pendingDelete) {
      return;
    }

    this.setState({ isDeleting: true });
    ccsdChangesDelete(pendingDelete.id, currentPage, this.getCombinedFilters())
      .then(() => this.setState({ pendingDelete: null, isDeleting: false }))
      .catch(() => this.setState({ isDeleting: false }));
  }

  render() {
    const { ccsdChanges, isFetchingCcsdChanges, pageCount, currentPage, ccsdChangesCheck, ccsdChangesUncheck, pageTitle } = this.props;
    const { activeTab, pendingDelete, isDeleting } = this.state;
    // Delete is consultant-tab only — a fee assured change moves the price for every consultant on the default
    const canDelete = isSeniorStaff();

    return (
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>{pageTitle}</span>
          {activeTab === 'consultant' && !isFetchingCcsdChanges && ccsdChanges && ccsdChanges.length > 0 && (
            <span className="tabular-data-result-count">{pageCount} pages</span>
          )}
        </Card.Header>
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={(tab) => this.setActiveTab(tab)} variant="pills" className="p-1 bg-gray-200 m-0">
            <Tab eventKey="consultant" title="Consultant Fee Changes">
              <div className="mediaccount-filter-wrapper p-3 d-flex justify-content-between align-items-center">
                <CcsdChangesFilterForm location={this.props.match.path} onResetPage={() => this.changePage(1)} />
                {ccsdChanges && ccsdChanges.length > 0 && (() => {
                  const uncheckedIds = ccsdChanges.filter((dc) => !dc.checkedAt).map((dc) => dc.id);
                  return (
                    <button
                      className="btn btn-sm btn-outline-success ml-3 flex-shrink-0"
                      disabled={uncheckedIds.length === 0}
                      title={`Mark all ${uncheckedIds.length} unchecked item${uncheckedIds.length !== 1 ? 's' : ''} on this page as checked`}
                      onClick={() => uncheckedIds.length > 0 && this.handleBulkCheck(uncheckedIds)}
                    >
                      Mark all as checked ({uncheckedIds.length})
                    </button>
                  );
                })()}
              </div>

              {isFetchingCcsdChanges && (
                <div className="p-3">
                  <MASpinner />
                </div>
              )}

              {!isFetchingCcsdChanges && (!ccsdChanges || ccsdChanges.length === 0) && (
                <div className="p-3">
                  <Alert variant="info" className="mb-0">
                    No price changes found
                  </Alert>
                </div>
              )}

              {!isFetchingCcsdChanges && ccsdChanges && ccsdChanges.length > 0 && (
                <CcsdChangesTable
                  ccsdChanges={ccsdChanges}
                  onCheck={ccsdChangesCheck}
                  onUncheck={ccsdChangesUncheck}
                  onDelete={canDelete ? (datedCost) => this.setState({ pendingDelete: datedCost }) : undefined}
                />
              )}

              {!isFetchingCcsdChanges && ccsdChanges && ccsdChanges.length > 0 && (
                <Paginator
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setPage={this.changePage.bind(this)}
                  nextPage={this.nextPage.bind(this)}
                  prevPage={this.prevPage.bind(this)}
                />
              )}
            </Tab>

            <Tab eventKey="feeAssured" title="Fee Assured Changes">
              <CcsdDefaultChangesTab />
            </Tab>
          </Tabs>
        </Card.Body>

        <DeleteCostChangeModal
          datedCost={pendingDelete}
          show={!!pendingDelete}
          isDeleting={isDeleting}
          onConfirm={() => this.confirmDelete()}
          onCancel={() => this.setState({ pendingDelete: null })}
        />
      </Card>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CcsdChangesContainer);
